import logger from "./logger";

const HEIC_MIME_TYPES = [
  "image/heic",
  "image/heif",
  "image/heic-sequence",
  "image/heif-sequence",
];

const HEIC_EXTENSIONS = [".heic", ".heif"];

const JPEG_QUALITY = 0.85;

/**
 * Browsers other than Safari cannot render HEIC/HEIF, so previews and stored
 * images come out blank. Some browsers also report an empty MIME type for
 * these files, hence the extension fallback.
 */
export const isHeic = (file: File): boolean =>
  HEIC_MIME_TYPES.includes(file.type.toLowerCase()) ||
  HEIC_EXTENSIONS.some((ext) => file.name.toLowerCase().endsWith(ext));

const toJpegName = (name: string) => name.replace(/\.(heic|heif)$/i, "") + ".jpg";

/**
 * Converts a HEIC/HEIF file to JPEG so it can be previewed and viewed after
 * upload. Non-HEIC files are returned untouched. If the conversion fails the
 * original file is returned so the upload can still go through.
 */
export const convertHeicToJpeg = async (file: File): Promise<File> => {
  if (!isHeic(file)) {
    return file;
  }

  try {
    // Loaded on demand — the decoder is ~3MB and most users never pick a HEIC.
    const { heicTo } = await import("heic-to");
    const blob = await heicTo({
      blob: file,
      type: "image/jpeg",
      quality: JPEG_QUALITY,
    });

    return new File([blob], toJpegName(file.name), {
      type: "image/jpeg",
      lastModified: file.lastModified,
    });
  } catch (error) {
    logger.error("HEIC conversion failed:", error);
    return file;
  }
};
